/**
 * ProposalChatPanel
 * =================
 * Floating chat widget for client proposal AI Q&A.
 * Dark/gold themed to match the proposal deck page.
 */

import React, { useState, useRef, useEffect, useCallback } from 'react';
import ReactMarkdown from 'react-markdown';
import { streamProposalChatMessage, getProposalChatHistory } from '../../api/publicProposals';

/* Brand tokens (must match ClientProposalReviewPage Z tokens) */
const Z = {
  bg: '#FFFFFF',
  bgCard: '#FFFFFF',
  bgCardHover: '#F3F4F6',
  gold: '#21436b',
  goldLight: '#2a5080',
  goldDark: '#1a365d',
  heading: '#21436b',
  white: '#FFFFFF',
  gray100: '#F5F5F5',
  gray400: 'rgba(33,67,107,0.7)',
  gray600: 'rgba(33,67,107,0.55)',
  gray800: '#E5E7EB',
  border: 'rgba(33,67,107,0.12)',
  borderSubtle: '#e6edf1',
};

const SUGGESTED_QUESTIONS = [
  'What is the proposed investment structure?',
  'Explain the risk factors',
  'Summarize the key terms',
];

const ProposalChatPanel = ({ isOpen, onClose, token }) => {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [sessionId] = useState(() => {
    const storageKey = `proposal_chat_session_${token}`;
    const stored = localStorage.getItem(storageKey);
    if (stored) return stored;
    const newId = `pchat_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
    localStorage.setItem(storageKey, newId);
    return newId;
  });
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const historyLoaded = useRef(false);
  const abortRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Load existing chat history on first open
  useEffect(() => {
    if (isOpen && !historyLoaded.current && sessionId && token) {
      historyLoaded.current = true;
      getProposalChatHistory(token, sessionId).then(res => {
        const existing = res.data?.messages || [];
        if (existing.length > 0) {
          setMessages(existing.map(m => ({
            role: m.role,
            content: m.content,
            id: m.id,
          })));
        }
      }).catch(() => {});
    }
  }, [isOpen, sessionId, token]);

  useEffect(() => {
    if (isOpen) {
      inputRef.current?.focus();
    }
  }, [isOpen]);

  // Abort in-flight stream on unmount
  useEffect(() => {
    return () => abortRef.current?.abort();
  }, []);

  const handleSend = useCallback(async (text) => {
    const msg = (text || input).trim();
    if (!msg || sending) return;

    const userId = Date.now();
    const assistantId = userId + 1;

    setMessages(prev => [...prev, { role: 'user', content: msg, id: userId }]);
    setInput('');
    setSending(true);

    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    await streamProposalChatMessage(token, sessionId, msg, {
      signal: controller.signal,
      onToken: (chunk) => {
        setMessages(prev => {
          const existing = prev.find(m => m.id === assistantId);
          if (existing) {
            return prev.map(m =>
              m.id === assistantId ? { ...m, content: m.content + chunk } : m
            );
          }
          return [...prev, { role: 'assistant', content: chunk, id: assistantId, isStreaming: true }];
        });
      },
      onDone: () => {
        setMessages(prev => prev.map(m =>
          m.id === assistantId ? { ...m, isStreaming: false } : m
        ));
        setSending(false);
      },
      onError: () => {
        setMessages(prev => {
          const existing = prev.find(m => m.id === assistantId);
          if (existing) {
            return prev.map(m =>
              m.id === assistantId
                ? { ...m, content: m.content || 'Sorry, I encountered an error. Please try again.', isError: true, isStreaming: false }
                : m
            );
          }
          return [...prev, { role: 'assistant', content: 'Sorry, I encountered an error. Please try again.', id: assistantId, isError: true }];
        });
        setSending(false);
      },
    });
  }, [input, sending, token, sessionId]);

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className={`fixed bottom-6 right-6 flex flex-col z-50 overflow-hidden transition-all duration-300 ${
        expanded ? 'w-[36rem] h-[50rem]' : 'w-96 h-[50rem]'
      }`}
      style={{
        background: Z.bgCard,
        border: `1px solid ${Z.border}`,
        borderRadius: 22,
        boxShadow: '0 4px 20px rgba(0,0,0,0.1), 0 0 0 1px rgba(33,67,107,0.06)',
      }}
    >
      {/* Header */}
      <div
        className="flex items-center justify-between px-4 py-3 flex-shrink-0"
        style={{
          borderBottom: `1px solid ${Z.border}`,
          background: `linear-gradient(180deg, ${Z.bgCardHover} 0%, ${Z.bgCard} 100%)`,
        }}
      >
        <div className="flex items-center gap-2.5">
          <div
            className="w-8 h-8 rounded-full flex items-center justify-center"
            style={{ background: `linear-gradient(135deg, ${Z.gold}, ${Z.goldDark})` }}
          >
            <svg className="w-4 h-4" fill="none" stroke="#fff" strokeWidth={2} viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09zM18.259 8.715L18 9.75l-.259-1.035a3.375 3.375 0 00-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 002.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 002.455 2.456L21.75 6l-1.036.259a3.375 3.375 0 00-2.455 2.456z" />
            </svg>
          </div>
          <div>
            <h3 className="text-sm font-semibold" style={{ color: Z.heading }}>Ask Zamanat</h3>
            <p className="text-xs" style={{ color: Z.gray600 }}>Ask questions about this proposal</p>
          </div>
        </div>
        <div className="flex items-center gap-1">
          <button
            onClick={() => setExpanded(e => !e)}
            title={expanded ? 'Collapse' : 'Expand'}
            className="p-1.5 rounded-lg transition-colors"
            style={{ color: Z.gray400 }}
            onMouseEnter={e => { e.currentTarget.style.color = Z.white; e.currentTarget.style.background = 'rgba(0,0,0,0.04)'; }}
            onMouseLeave={e => { e.currentTarget.style.color = Z.gray400; e.currentTarget.style.background = 'transparent'; }}
          >
            {expanded ? (
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 9L4 4m0 0v4m0-4h4m6 6l5 5m0 0v-4m0 4h-4" />
              </svg>
            ) : (
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 3h6m0 0v6m0-6L14 10M9 21H3m0 0v-6m0 6l7-7" />
              </svg>
            )}
          </button>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg transition-colors"
            style={{ color: Z.gray400 }}
            onMouseEnter={e => { e.currentTarget.style.color = Z.white; e.currentTarget.style.background = 'rgba(0,0,0,0.04)'; }}
            onMouseLeave={e => { e.currentTarget.style.color = Z.gray400; e.currentTarget.style.background = 'transparent'; }}
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>
        </div>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3" style={{ scrollbarWidth: 'thin', scrollbarColor: `${Z.gray800} transparent` }}>
        {messages.length === 0 && (
          <div className="text-center py-8">
            <div
              className="w-12 h-12 rounded-full mx-auto mb-3 flex items-center justify-center"
              style={{ background: `linear-gradient(135deg, rgba(33,67,107,0.1), rgba(33,67,107,0.04))`, border: `1px solid ${Z.border}` }}
            >
              <svg className="w-6 h-6" style={{ color: Z.gold }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
              </svg>
            </div>
            <p className="text-sm mb-1" style={{ color: Z.gray400 }}>Ask Zamanat something</p>
            <p className="text-xs mb-4" style={{ color: Z.gray600 }}>I can help explain terms, structure, and details</p>
            <div className="space-y-2">
              {SUGGESTED_QUESTIONS.map(q => (
                <button
                  key={q}
                  onClick={() => handleSend(q)}
                  className="block w-full text-left px-3 py-2 text-xs rounded-lg transition-all"
                  style={{
                    color: Z.goldLight,
                    background: 'rgba(33,67,107,0.06)',
                    border: `1px solid ${Z.border}`,
                  }}
                  onMouseEnter={e => { e.currentTarget.style.background = 'rgba(33,67,107,0.1)'; }}
                  onMouseLeave={e => { e.currentTarget.style.background = 'rgba(33,67,107,0.06)'; }}
                >
                  {q}
                </button>
              ))}
            </div>
          </div>
        )}

        {messages.map(msg => (
          <div key={msg.id} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
            <div
              className="max-w-[85%] rounded-xl px-3 py-2"
              style={msg.role === 'user'
                ? { background: `linear-gradient(135deg, ${Z.gold}, ${Z.goldDark})`, color: '#fff' }
                : msg.isError
                  ? { background: 'rgba(239,68,68,0.1)', color: '#EF4444', border: '1px solid rgba(239,68,68,0.2)' }
                  : { background: Z.bgCardHover, color: Z.heading, border: `1px solid ${Z.borderSubtle}` }
              }
            >
              {msg.role === 'assistant' && !msg.isError ? (
                <div className="text-sm prose-invert prose-sm max-w-none [&_p]:mb-1 [&_p:last-child]:mb-0 [&_ul]:mt-1 [&_li]:text-sm [&_strong]:text-white [&_em]:text-[#E8C97A]">
                  <ReactMarkdown>{msg.content}</ReactMarkdown>
                </div>
              ) : (
                <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
              )}
            </div>
          </div>
        ))}

        {sending && !messages.some(m => m.isStreaming) && (
          <div className="flex justify-start">
            <div className="rounded-xl px-3 py-2" style={{ background: Z.bgCardHover, border: `1px solid ${Z.borderSubtle}` }}>
              <div className="flex items-center gap-1.5">
                <div className="w-2 h-2 rounded-full animate-bounce" style={{ background: Z.gold, animationDelay: '0ms' }} />
                <div className="w-2 h-2 rounded-full animate-bounce" style={{ background: Z.gold, animationDelay: '150ms' }} />
                <div className="w-2 h-2 rounded-full animate-bounce" style={{ background: Z.gold, animationDelay: '300ms' }} />
              </div>
            </div>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <div className="px-4 py-3 flex-shrink-0" style={{ borderTop: `1px solid ${Z.border}` }}>
        <div className="flex items-end gap-2">
          <textarea
            ref={inputRef}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Ask about this proposal..."
            rows={1}
            className="flex-1 resize-none px-3 py-2 rounded-lg text-sm transition-all focus:outline-none max-h-20"
            style={{
              background: Z.bgCardHover,
              border: `1px solid ${Z.borderSubtle}`,
              color: Z.heading,
            }}
            onFocus={(e) => { e.target.style.borderColor = Z.gold; e.target.style.boxShadow = '0 0 0 3px rgba(33,67,107,0.1)'; }}
            onBlur={(e) => { e.target.style.borderColor = Z.borderSubtle; e.target.style.boxShadow = 'none'; }}
          />
          <button
            onClick={() => handleSend()}
            disabled={!input.trim() || sending}
            className="flex-shrink-0 p-2 rounded-lg transition-all disabled:opacity-30 disabled:cursor-not-allowed"
            style={{
              background: `linear-gradient(135deg, ${Z.gold}, ${Z.goldDark})`,
              color: '#fff',
            }}
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
            </svg>
          </button>
        </div>
      </div>
    </div>
  );
};

export default ProposalChatPanel;
